import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Gift, TrendingUp, ChevronRight } from "lucide-react";
import { useSelector } from "react-redux";
import { UserModelData } from "../../types";
import useThemeHook from "../hooks/useThemeHook";
import { useNavigate } from "react-router-dom";
import { PAGE_NAME } from "../../types";

const STREAK_MILESTONES = [
  { days: 3, reward: "+10% ATH bonus on all activities", icon: "🔥" },
  { days: 7, reward: "Free Gold Mystery Box", icon: "🎁" },
  { days: 14, reward: "Free Turbo Charge (5x mining)", icon: "⚡" },
  { days: 30, reward: "1 day of Pro FREE", icon: "👑" },
];

const DepositStreakCard: React.FC = () => {
  const theme = useThemeHook();
  const navigate = useNavigate();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const depositStreak = user?.depositStreak || 0;

  // Find next milestone
  const nextMilestone = STREAK_MILESTONES.find((m) => m.days > depositStreak);
  const daysToNext = nextMilestone ? nextMilestone.days - depositStreak : 0;

  // Check if user deposited today
  const [depositedToday, setDepositedToday] = useState(false);
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const lastDeposit = user?.lastDepositDate;
    if (lastDeposit === today) {
      setDepositedToday(true);
    }
  }, [user?.lastDepositDate]);

  if (depositStreak === 0 && !depositedToday) {
    // Show teaser for non-depositors
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={() => navigate(PAGE_NAME.DEPOSIT)}
        className={`p-4 rounded-2xl border cursor-pointer active:scale-[0.98] transition-all ${
          theme === "dark"
            ? "bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border-emerald-500/30"
            : "bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              theme === "dark" ? "bg-emerald-500/20" : "bg-emerald-100"
            }`}
          >
            <TrendingUp size={20} className="text-emerald-500" />
          </div>
          <div className="flex-1">
            <p
              className={`font-bold text-sm ${
                theme === "dark" ? "text-emerald-300" : "text-emerald-800"
              }`}
            >
              Start a Deposit Streak! 🔥
            </p>
            <p
              className={`text-[10px] ${
                theme === "dark" ? "text-emerald-400/70" : "text-emerald-600"
              }`}
            >
              Deposit 1+ Pi daily to unlock exclusive bonuses
            </p>
          </div>
          <ChevronRight
            size={18}
            className={theme === "dark" ? "text-emerald-500" : "text-emerald-400"}
          />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-2xl border ${
        theme === "dark"
          ? "bg-gradient-to-r from-orange-900/30 to-amber-900/30 border-orange-500/30"
          : "bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Flame
            size={18}
            className="text-orange-500"
            fill="currentColor"
          />
          <span
            className={`font-black text-sm ${
              theme === "dark" ? "text-orange-300" : "text-orange-800"
            }`}
          >
            {depositStreak} Day Deposit Streak
          </span>
        </div>
        {depositedToday ? (
          <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-black">
            ✓ TODAY
          </span>
        ) : (
          <button
            onClick={() => navigate(PAGE_NAME.DEPOSIT)}
            className="px-3 py-1 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[10px] font-bold active:scale-95 transition-all"
          >
            Deposit to keep streak!
          </button>
        )}
      </div>

      {/* Milestones */}
      <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbars">
        {STREAK_MILESTONES.map((m) => (
          <div
            key={m.days}
            className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-center min-w-[80px] ${
              depositStreak >= m.days
                ? theme === "dark"
                  ? "bg-emerald-500/20 border border-emerald-500/30"
                  : "bg-emerald-100 border border-emerald-300"
                : theme === "dark"
                ? "bg-slate-800/50 border border-slate-700"
                : "bg-white/60 border border-gray-200"
            }`}
          >
            <span className="text-lg">{m.icon}</span>
            <p
              className={`text-[9px] font-bold ${
                depositStreak >= m.days
                  ? "text-emerald-600"
                  : theme === "dark"
                  ? "text-slate-400"
                  : "text-gray-500"
              }`}
            >
              Day {m.days}
            </p>
          </div>
        ))}
      </div>

      {nextMilestone && (
        <p
          className={`text-[10px] mt-2 ${
            theme === "dark" ? "text-orange-400/70" : "text-orange-600"
          }`}
        >
          {daysToNext} more day{daysToNext !== 1 ? "s" : ""} to unlock:{" "}
          <strong>{nextMilestone.reward}</strong>
        </p>
      )}
    </motion.div>
  );
};

export default DepositStreakCard;
